import React, { useEffect } from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	useConversationHeaderSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { RovoConversationTitleBlock } from '@atlassian/rovo-platform-ui-components/conversation-title-block';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { TAGS } from '../../controllers/chat-store/types';
import { useCurrentConversation } from '../../controllers/chat-store/useCurrentConversation';
import { useCurrentConversationId } from '../../controllers/chat-store/useCurrentConversationId';

const TAGS_ELIGIBLE_FOR_TITLE_BLOCK: string[] = [TAGS.INSIGHTS];

function ConversationTitleBlockContainerInner({
	currentConversationId,
	conversation,
	conversationHeader,
}: {
	currentConversationId: string;
	conversation: ReturnType<typeof useCurrentConversation>;
	conversationHeader: ReturnType<typeof useConversationHeaderSelector>[0]['conversationHeader'];
}): React.ReactElement | null {
	const { fullScreen } = useUIConfig();
	const { setConversationHeader } = useUIStoreActions();

	const header = conversationHeader ?? null;
	const headerForThisChat = header?.conversationId === currentConversationId;

	const hasEligibleTag =
		conversation?.tags?.some((tag) => TAGS_ELIGIBLE_FOR_TITLE_BLOCK.includes(tag)) ?? false;
	// title for when an insights is viewed in persisted state (conversation was initiated)
	const persistedTitle = hasEligibleTag ? (conversation?.name?.trim() ?? '') : '';
	// title for when insights is viewed in the preview state
	const headerTitle = headerForThisChat ? (header?.title?.trim() ?? '') : '';
	const title = persistedTitle || headerTitle;
	const showTitleBlock = (hasEligibleTag && persistedTitle.length > 0) || headerTitle.length > 0;

	useEffect(() => {
		if (header && header.conversationId !== currentConversationId) {
			setConversationHeader(null);
		}
	}, [header, currentConversationId, setConversationHeader]);

	if (!showTitleBlock) {
		return null;
	}

	return (
		<RovoConversationTitleBlock
			title={title}
			icon={headerForThisChat ? header?.icon : undefined}
			iconAppearance={headerForThisChat ? header?.iconAppearance : undefined}
			isFullScreen={fullScreen}
		/>
	);
}

type ConversationTitleBlockUIProps = {
	currentConversationId: string;
	conversation: ReturnType<typeof useCurrentConversation>;
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedConversationTitleBlockContainerInner = React.memo(
	ConversationTitleBlockContainerInner,
	shallowEqualObjects,
);

// rovo_ttai_segment_render_stabilization ON: subscribe to `conversationHeader` via
// the narrow selector so the component only re-renders when `conversationHeader`
// changes, rather than on every UI-store state change.
const ConversationTitleBlockWithSelector = (
	props: ConversationTitleBlockUIProps,
): React.ReactElement | null => {
	di(useConversationHeaderSelector);
	const [{ conversationHeader }] = useConversationHeaderSelector();
	return (
		<MemoizedConversationTitleBlockContainerInner
			{...props}
			conversationHeader={conversationHeader}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const ConversationTitleBlockWithBroadStore = (
	props: ConversationTitleBlockUIProps,
): React.ReactElement | null => {
	di(useUIStore);
	const { conversationHeader } = useUIStore();
	return (
		<ConversationTitleBlockContainerInner {...props} conversationHeader={conversationHeader} />
	);
};

const ConversationTitleBlockUI = (
	props: ConversationTitleBlockUIProps,
): React.ReactElement | null => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<ConversationTitleBlockWithSelector {...props} />
	) : (
		<ConversationTitleBlockWithBroadStore {...props} />
	);
};

export function ConversationTitleBlockContainer(): React.ReactElement | null {
	const currentConversationId = useCurrentConversationId();
	const conversation = useCurrentConversation();
	return (
		<ConversationTitleBlockUI
			currentConversationId={currentConversationId}
			conversation={conversation}
		/>
	);
}
